import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  Mail,
  Phone,
  MapPin,
  Facebook,
  Instagram,
  Twitter,
  Heart,
  ShieldCheck,
  Truck,
  BadgeCheck,
} from "lucide-react";
import logoSvg from "@/assets/images/logo.svg";
import naSystemsSvg from "@/assets/images/na-systems.svg";

export function Footer() {
  const { t, i18n } = useTranslation();
  const { lang } = useParams<{ lang: string }>();
  const navigate = useNavigate();
  const isRtl = i18n.dir() === "rtl";

  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gradient-to-b from-background to-secondary/30 border-t mt-auto">
      <div className="container mx-auto px-4 py-12 md:py-16 max-w-7xl">
        {/* Brand + Trust Row */}
        <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1.8fr] gap-8 items-start mb-10">
          <div className="text-left">
            <div className="flex items-center gap-4">
              <div className="relative p-3 rounded-xl">
                <img
                  src={logoSvg}
                  alt="Kosher Market Logo"
                  width={2048}
                  height={726}
                  className="h-12 w-auto logo-blue-filter"
                />
              </div>
            </div>
          </div>

          {/* <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="glass-panel rounded-2xl px-4 py-3 flex items-center gap-3">
              <ShieldCheck className="h-5 w-5 text-primary" />
              <div>
                <p className="text-sm font-semibold text-foreground">
                  {t("footer.trust.certified", "Certified Kosher")}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t("footer.trust.supervised", "Rabbinical supervision")}
                </p>
              </div>
            </div>
            <div className="glass-panel rounded-2xl px-4 py-3 flex items-center gap-3">
              <Truck className="h-5 w-5 text-primary" />
              <div>
                <p className="text-sm font-semibold text-foreground">
                  {t("footer.trust.delivery", "Reliable Delivery")}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t("footer.trust.windows", "Clear delivery windows")}
                </p>
              </div>
            </div>
            <div className="glass-panel rounded-2xl px-4 py-3 flex items-center gap-3">
              <BadgeCheck className="h-5 w-5 text-primary" />
              <div>
                <p className="text-sm font-semibold text-foreground">
                  {t("footer.trust.secure", "Secure Payments")}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t("footer.trust.protected", "Encrypted checkout")}
                </p>
              </div>
            </div>
          </div> */}
        </div>

        <div
          className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12 ${
            isRtl ? "text-right" : "text-left"
          }`}
        >
          {/* Company Info */}
          <div>
            <h2 className="text-lg font-semibold mb-4 text-foreground flex items-center gap-2">
              <div className="h-1 w-8 bg-primary rounded"></div>
              {t("footer.company.title")}
            </h2>
            <ul className="space-y-2">
              <li>
                <button
                  onClick={() => {
                    const currentLang = lang || i18n.language;
                    navigate(`/${currentLang}/about`);
                  }}
                  className="text-sm text-muted-foreground hover:text-primary transition-colors hover:translate-x-1 inline-block"
                >
                  {t("footer.quickLinks.about")}
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    const currentLang = lang || i18n.language;
                    navigate(`/${currentLang}/discounts`);
                  }}
                  className="text-sm text-muted-foreground hover:text-primary transition-colors hover:translate-x-1 inline-block"
                >
                  {t("nav.discounts")}
                </button>
              </li>
            </ul>

            {/* Social Links */}
            <div className="mt-6">
              <p className="text-sm font-medium mb-3 text-foreground">
                {t("footer.followUs")}
              </p>
              <div className="flex gap-3">
                {/* <a
                  href="https://facebook.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-primary transition-all hover:scale-110 p-2 rounded-lg hover:bg-primary/10"
                  aria-label="Facebook"
                >
                  <Facebook size={20} />
                </a> */}
                <a
                  href="https://instagram.com/koshermarketcancun/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-primary transition-all hover:scale-110 p-2 rounded-lg hover:bg-primary/10"
                  aria-label="Instagram"
                >
                  <Instagram size={20} />
                </a>
                {/* <a
                  href="https://twitter.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-primary transition-all hover:scale-110 p-2 rounded-lg hover:bg-primary/10"
                  aria-label="Twitter"
                >
                  <Twitter size={20} />
                </a> */}
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h2 className="text-lg font-semibold mb-4 text-foreground flex items-center gap-2">
              <div className="h-1 w-8 bg-primary rounded"></div>
              {t("footer.quickLinks.title")}
            </h2>
            <ul className="space-y-2">
              <li>
                <button
                  onClick={() => {
                    const currentLang = lang || i18n.language;
                    navigate(`/${currentLang}`);
                  }}
                  className="text-sm text-muted-foreground hover:text-primary transition-colors hover:translate-x-1 inline-block"
                >
                  {t("footer.quickLinks.home")}
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    const currentLang = lang || i18n.language;
                    navigate(`/${currentLang}/products`);
                  }}
                  className="text-sm text-muted-foreground hover:text-primary transition-colors hover:translate-x-1 inline-block"
                >
                  {t("footer.quickLinks.products")}
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    const currentLang = lang || i18n.language;
                    navigate(`/${currentLang}/login`);
                  }}
                  className="text-sm text-muted-foreground hover:text-primary transition-colors hover:translate-x-1 inline-block"
                >
                  {t("footer.quickLinks.account")}
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    const currentLang = lang || i18n.language;
                    navigate(`/${currentLang}/dashboard`);
                  }}
                  className="text-sm text-muted-foreground hover:text-primary transition-colors hover:translate-x-1 inline-block"
                >
                  {t("nav.dashboard")}
                </button>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h2 className="text-lg font-semibold mb-4 text-foreground flex items-center gap-2">
              <div className="h-1 w-8 bg-primary rounded"></div>
              {t("footer.legal.title")}
            </h2>
            <ul className="space-y-2">
              <li>
                <button
                  onClick={() => {
                    const currentLang = lang || i18n.language;
                    navigate(`/${currentLang}/privacy`);
                  }}
                  className="text-sm text-muted-foreground hover:text-primary transition-colors hover:translate-x-1 inline-block"
                >
                  {t("footer.legal.privacy")}
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    const currentLang = lang || i18n.language;
                    navigate(`/${currentLang}/terms`);
                  }}
                  className="text-sm text-muted-foreground hover:text-primary transition-colors hover:translate-x-1 inline-block"
                >
                  {t("footer.legal.terms")}
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    const currentLang = lang || i18n.language;
                    navigate(`/${currentLang}/refund`);
                  }}
                  className="text-sm text-muted-foreground hover:text-primary transition-colors hover:translate-x-1 inline-block"
                >
                  {t("footer.legal.refund")}
                </button>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h2 className="text-lg font-semibold mb-4 text-foreground flex items-center gap-2">
              <div className="h-1 w-8 bg-primary rounded"></div>
              {t("footer.contact.title")}
            </h2>
            <ul className="space-y-3">
              <li
                className={`flex items-start gap-2 ${
                  isRtl ? "flex-row-reverse" : ""
                }`}
              >
                <MapPin
                  size={18}
                  className="text-primary mt-0.5 flex-shrink-0"
                />
                <a
                  href="https://www.google.com/maps/search/?api=1&query=Cancún,+Plaza+Córner+Center+Local+123"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  {t("footer.contact.addressLine")}
                </a>
              </li>
              <li
                className={`flex items-center gap-2 ${
                  isRtl ? "flex-row-reverse" : ""
                }`}
              >
                <Phone size={18} className="text-primary flex-shrink-0" />
                <a
                  href="tel:+529985505030"
                  dir="ltr"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors text-a"
                >
                  +52 998 550 5030
                </a>
              </li>
              <li
                className={`flex items-center gap-2 ${
                  isRtl ? "flex-row-reverse" : ""
                }`}
              >
                <Mail size={18} className="text-primary flex-shrink-0" />
                <a
                  href="mailto:support@koshermarket.mx"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  support@koshermarket.mx
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t mt-12 pt-8">
          <div
            className={`flex flex-col md:flex-row justify-between items-center gap-4 ${
              isRtl ? "md:flex-row-reverse" : ""
            }`}
          >
            <p className="text-sm text-muted-foreground text-center md:text-left">
              © {currentYear} {t("footer.copyright")}
            </p>
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <span className="text-xs">{t("footer.developedBy")}</span>
              <a
                href="https://www.na-systems.tech/"
                target="_blank"
                rel="noopener noreferrer"
                className="opacity-60 hover:opacity-100 transition-opacity dark:invert"
                aria-label="NA.Systems"
              >
                <img
                  src={naSystemsSvg}
                  alt="NA.Systems"
                  className="h-4 w-auto"
                />
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
